import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart,
  ShieldCheck,
  Users,
  Globe,
  ArrowRight,
  CheckCircle2,
  HandCoins,
  Building2,
  CreditCard,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const IMAGES = {
  hero: '/assets/images/donate-hero.jpg',
  impact: '/assets/images/donate-impact.jpg',
  child: '/assets/images/donate-child.jpg',
};

const STRIPE_DONATION_LINK = 'https://buy.stripe.com/3cI28jaj42tvghX7xigw002';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const INITIATIVE_CONFIG = {
  hpc: {
    key: 'hpc',
    title: 'His Presence Chapel Inc.',
    shortTitle: 'HPC',
    badge: 'Support Our Mission',
    heroTitleTop: 'Give Boldly.',
    heroTitleBottom: 'Transform Lives.',
    heroText:
      'Your donation helps His Presence Chapel Inc. advance humanitarian outreach, faith-driven service, and sustainable community impact through a strong and centralized nonprofit ecosystem.',
    pageLead:
      'Every gift strengthens compassionate outreach, empowerment, dignity, and long-term transformation through His Presence Chapel Inc.',
    whyTitle: 'Why Your Donation Matters',
    whyText:
      'Your support fuels practical interventions, hope-centered service, and meaningful impact in the lives of individuals, families, and communities.',
    cards: [
      {
        icon: Heart,
        title: 'Humanitarian Support',
        description:
          'Help provide relief, care, and urgent support for vulnerable individuals and families facing real need.',
        color: '#F15A24',
      },
      {
        icon: Users,
        title: 'Empowerment Programs',
        description:
          'Support initiatives that build skills, restore confidence, and create opportunities for sustainable growth.',
        color: '#F7B500',
      },
      {
        icon: Globe,
        title: 'Community Development',
        description:
          'Invest in outreach and support systems that strengthen dignity, belonging, resilience, and hope.',
        color: '#6EC1E4',
      },
    ],
    impactPoints: [
      'Community outreach and relief support',
      'Programs for empowerment and resilience',
      'Faith-based care and compassionate service',
      'Operational support for sustainable impact',
    ],
    destinationTitle: 'Donate to His Presence Chapel Inc.',
    destinationText:
      'Your donation will be processed securely through Stripe and directed to support the mission and impact work of HPC.',
    stripeButtonText: 'Donate Securely Now',
    partnerLink: '/partnership',
  },

  seeal: {
    key: 'seeal',
    title: 'SEEAL Africa',
    shortTitle: 'SEEAL Africa',
    badge: 'Support SEEAL Africa',
    heroTitleTop: 'Empower Dreams.',
    heroTitleBottom: 'Build Opportunity.',
    heroText:
      'Your support helps SEEAL Africa expand skills development, enterprise support, mentorship, and practical pathways for economic growth and dignity.',
    pageLead:
      'Every gift helps SEEAL Africa unlock opportunities, strengthen livelihoods, and empower people for sustainable advancement.',
    whyTitle: 'Why Your Donation Matters for SEEAL Africa',
    whyText:
      'Your giving supports practical empowerment programs that help individuals and communities grow with confidence, capacity, and opportunity.',
    cards: [
      {
        icon: Users,
        title: 'Skills Development',
        description:
          'Help equip individuals with practical knowledge, learning access, and growth opportunities for a better future.',
        color: '#0B5FA5',
      },
      {
        icon: Building2,
        title: 'Enterprise Support',
        description:
          'Support initiatives that encourage entrepreneurship, financial independence, and local innovation.',
        color: '#3CB54A',
      },
      {
        icon: Globe,
        title: 'Mentorship & Access',
        description:
          'Enable mentorship, strategic support, and access-building pathways that create long-term progress.',
        color: '#2EA9DF',
      },
    ],
    impactPoints: [
      'Skills and vocational development initiatives',
      'Enterprise and livelihood support programs',
      'Mentorship and growth opportunities',
      'Sustainable empowerment pathways',
    ],
    destinationTitle: 'Donate to SEEAL Africa',
    destinationText:
      'Your donation will be processed securely through Stripe and support empowerment, enterprise, and opportunity-building programs.',
    stripeButtonText: 'Support SEEAL Africa',
    partnerLink: '/partnership?initiative=seeal',
  },

  rahvai: {
    key: 'rahvai',
    title: 'Rehabilitation and Hope for the Vulnerable in Africa Initiative',
    shortTitle: 'RAHVAI',
    badge: 'Support RAHVAI',
    heroTitleTop: 'Restore Hope.',
    heroTitleBottom: 'Stand for the Vulnerable.',
    heroText:
      'Your support helps RAHVAI respond to humanitarian needs through relief, water access, empowerment, protection, and community-centered care across Africa.',
    pageLead:
      'Every gift helps RAHVAI strengthen dignity, resilience, healing, and hope among vulnerable individuals and communities.',
    whyTitle: 'Why Your Donation Matters for RAHVAI',
    whyText:
      'Your giving sustains humanitarian action that responds to urgent needs while building long-term resilience and transformation.',
    cards: [
      {
        icon: Heart,
        title: 'Humanitarian Relief',
        description:
          'Help provide practical support to vulnerable individuals, families, and communities facing hardship.',
        color: '#2D3E55',
      },
      {
        icon: Globe,
        title: 'Water Access Initiatives',
        description:
          'Support clean water access and community infrastructure that improves health and quality of life.',
        color: '#6EC1E4',
      },
      {
        icon: Users,
        title: 'Youth & Women Empowerment',
        description:
          'Invest in empowerment efforts that help people build skills, confidence, and economic opportunity.',
        color: '#F7B500',
      },
    ],
    impactPoints: [
      'Humanitarian outreach and relief support',
      'Water access and community well-being initiatives',
      'Youth and women empowerment programs',
      'Community development and resilience building',
    ],
    destinationTitle: 'Donate to RAHVAI',
    destinationText:
      'Your donation will be processed securely through Stripe and support relief, dignity, and hope-building interventions.',
    stripeButtonText: 'Support RAHVAI',
    partnerLink: '/partnership?initiative=rahvai',
  },
};

export default function DonatePage() {
  const [searchParams] = useSearchParams();

  const initiativeParam = (searchParams.get('initiative') || 'hpc').toLowerCase();
  const initiative = INITIATIVE_CONFIG[initiativeParam] || INITIATIVE_CONFIG.hpc;

  const stripeCheckoutLink = useMemo(() => {
    const url = new URL(STRIPE_DONATION_LINK);
    url.searchParams.set('prefilled_promo_code', '');
    return url.toString();
  }, []);

  const brandColor =
    initiative.key === 'seeal'
      ? '#0B5FA5'
      : initiative.key === 'rahvai'
      ? '#2D3E55'
      : '#F15A24';

  const softColor =
    initiative.key === 'seeal'
      ? 'rgba(11,95,165,0.10)'
      : initiative.key === 'rahvai'
      ? 'rgba(45,62,85,0.10)'
      : 'rgba(241,90,36,0.10)';

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ backgroundColor: softColor, color: brandColor }}
              >
                <Heart size={16} />
                {initiative.badge}
              </span>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D3E55] tracking-tight mb-6 leading-tight">
                {initiative.heroTitleTop}
                <br />
                <span style={{ color: brandColor }}>{initiative.heroTitleBottom}</span>
              </h1>

              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                {initiative.heroText}
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a href={stripeCheckoutLink} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="text-white px-8 h-14 text-base font-semibold"
                    style={{ backgroundColor: brandColor }}
                  >
                    Donate Now
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </a>

                <Link to={initiative.partnerLink}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-[#2D3E55] text-[#2D3E55] hover:bg-[#2D3E55] hover:text-white px-8 h-14 text-base font-semibold"
                  >
                    Partner With Us
                  </Button>
                </Link>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="rounded-2xl bg-[#F8FAFC] p-4 border border-slate-200">
                  <ShieldCheck className="mb-2" size={20} style={{ color: brandColor }} />
                  <p className="text-sm font-semibold text-[#2D3E55]">Secure Giving</p>
                </div>

                <div className="rounded-2xl bg-[#F8FAFC] p-4 border border-slate-200">
                  <Users className="mb-2" size={20} style={{ color: brandColor }} />
                  <p className="text-sm font-semibold text-[#2D3E55]">Real Impact</p>
                </div>

                <div className="rounded-2xl bg-[#F8FAFC] p-4 border border-slate-200">
                  <Globe className="mb-2" size={20} style={{ color: brandColor }} />
                  <p className="text-sm font-semibold text-[#2D3E55]">Trusted Mission</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <img
                src={IMAGES.hero}
                alt={`${initiative.shortTitle} donation support`}
                className="w-full h-[380px] md:h-[500px] object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg border border-slate-200 p-5 max-w-xs">
                <p className="text-sm text-slate-500 mb-1">Your giving supports</p>
                <p className="font-heading text-xl font-semibold text-[#2D3E55] leading-snug">
                  {initiative.shortTitle === 'HPC'
                    ? 'Families, youth, and vulnerable communities'
                    : initiative.shortTitle === 'SEEAL Africa'
                    ? 'Empowerment, opportunity, and economic growth'
                    : 'Relief, dignity, and vulnerable communities'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
              {initiative.whyTitle}
            </h2>
            <p className="font-body text-lg text-slate-600">{initiative.whyText}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {initiative.cards.map((card) => (
              <Card
                key={card.title}
                className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 rounded-3xl"
              >
                <CardContent className="p-8">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${card.color}15` }}
                  >
                    <card.icon size={28} style={{ color: card.color }} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-4">
                    {card.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{card.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="donation-options" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-4">
                Make Your Donation
              </h2>

              <p className="font-body text-lg text-slate-600 mb-8">
                {initiative.pageLead}
              </p>

              <div className="space-y-5">
                <div className="rounded-3xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: softColor }}
                    >
                      <HandCoins size={22} style={{ color: brandColor }} />
                    </div>

                    <div>
                      <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-1">
                        {initiative.destinationTitle}
                      </h3>
                      <p className="text-slate-600">{initiative.destinationText}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-4 text-sm text-slate-600">
                    <p className="mb-1">
                      <strong>Initiative:</strong> {initiative.shortTitle}
                    </p>
                    <p className="mb-1">
                      <strong>Payment processor:</strong> Stripe
                    </p>
                    <p>
                      <strong>Giving flow:</strong> Fast, secure, and professional
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 p-6 shadow-sm bg-gradient-to-br from-white to-slate-50">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2D3E55]/10 flex items-center justify-center">
                      <CreditCard className="text-[#2D3E55]" size={22} />
                    </div>

                    <div>
                      <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-1">
                        Secure Online Giving
                      </h3>
                      <p className="text-slate-600">
                        Give confidently through our trusted Stripe donation platform.
                      </p>
                    </div>
                  </div>

                  <a href={stripeCheckoutLink} target="_blank" rel="noopener noreferrer">
                    <Button
                      className="w-full h-14 text-white text-base font-semibold"
                      style={{ backgroundColor: brandColor }}
                    >
                      {initiative.stripeButtonText}
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </a>
                </div>

                <div className="rounded-3xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-2">
                    Partnership & Major Giving
                  </h3>
                  <p className="text-slate-600 mb-4">
                    For sponsorship, institutional support, or strategic partnership, connect with our team directly.
                  </p>
                  <Link to={initiative.partnerLink}>
                    <Button variant="outline" className="border-[#2D3E55] text-[#2D3E55] font-semibold">
                      Partner With Us
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <img
                src={IMAGES.impact}
                alt={`${initiative.shortTitle} donation impact`}
                className="w-full h-[260px] object-cover rounded-3xl shadow-lg"
              />

              <Card className="overflow-hidden rounded-3xl border-0 shadow-sm">
                <CardContent className="p-8">
                  <h3 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-5">
                    What Your Support Makes Possible
                  </h3>

                  <div className="space-y-4">
                    {initiative.impactPoints.map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle2
                          className="mt-0.5"
                          size={20}
                          style={{ color: brandColor }}
                        />
                        <p className="text-slate-600">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <img
                src={IMAGES.child}
                alt={`${initiative.shortTitle} community beneficiary`}
                className="w-full h-[220px] object-cover rounded-3xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#2D3E55]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto text-white">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              Thank You for Standing With Us
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Every donation is a bold investment in hope, dignity, empowerment, and lasting transformation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={stripeCheckoutLink} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="text-white px-8 h-14 text-base font-semibold"
                  style={{
                    backgroundColor:
                      initiative.key === 'seeal'
                        ? '#0B5FA5'
                        : initiative.key === 'rahvai'
                        ? '#F7B500'
                        : '#F15A24',
                  }}
                >
                  Donate Now
                </Button>
              </a>

              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8 h-14 text-base font-semibold"
                >
                  Speak With Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}